import React, { useEffect, useState } from 'react';
import ReportDownload from '../../components/ReportDownload';
import { useParams, useNavigate } from 'react-router-dom';
import { eventsAPI, usersAPI, clientsAPI, projectsAPI } from '../../api/client';
import { EVENT_TYPES, EVENT_STATUS, RISK_LEVEL, TIMELINE_KEYS, STATUS_TRANSITIONS } from '../../lib/eventConstants';
import { useAuth } from '../../context/AuthContext';
import { ArrowLeft, Edit2, Users, FileText, ExternalLink } from 'lucide-react';
import EventStaffTab from '../../components/staff/EventStaffTab';
import EvaluationsTab from '../../components/events/EvaluationsTab';
// #UbicacionUnify — UbicacionTab reemplaza EmergencyLinksTab + NearbyServicesTab
import UbicacionTab from '../../components/UbicacionTab';
import { dossierAPI } from '../../api/traceability';
import { canWork, STAGES } from '../../lib/traceability';
import Dossier from '../../components/traceability/Dossier';
import Providers from '../../components/traceability/Providers';
import Records from '../../components/traceability/Records';
import { toast } from 'sonner';

function EventStatusBadge({ status }) {
  const cfg = EVENT_STATUS[status] || EVENT_STATUS.planeacion;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.bg}`}>
      <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function RiskBadge({ level }) {
  const cfg = RISK_LEVEL[level] || RISK_LEVEL.medio;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-bold border ${cfg.bg}`}>{cfg.label}</span>
  );
}

function Timeline({ dates }) {
  const now = new Date();
  const items = TIMELINE_KEYS.map(k => ({ ...k, date: dates?.[k.key] ? new Date(dates[k.key]) : null }));
  const nearestFuture = items.reduce((acc, item) => {
    if (!item.date) return acc;
    if (item.date > now && (!acc || item.date < acc.date)) return item;
    return acc;
  }, null);

  return (
    <div className="relative py-4">
      <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-slate-200 -translate-y-1/2 mx-8" />
      <div className="relative flex justify-between">
        {items.map((item) => {
          const isPast = item.date && item.date < now;
          const isCurrent = item === nearestFuture;
          const hasDt = Boolean(item.date);
          return (
            <div key={item.key} className="flex flex-col items-center gap-1 flex-1 relative">
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm z-10 ${
                isCurrent ? 'bg-soem-primary border-soem-primary text-white shadow-lg scale-110'
                  : isPast ? 'bg-slate-300 border-slate-300 text-white'
                  : hasDt ? 'bg-blue-50 border-blue-300 text-blue-600'
                  : 'bg-white border-slate-200 text-slate-300'
              }`}>
                {item.icon}
              </div>
              <div className="text-center">
                <p className={`text-xs font-medium ${isCurrent ? 'text-soem-primary' : isPast ? 'text-slate-400' : 'text-slate-600'}`}>
                  {item.label}
                </p>
                {item.date && (
                  <p className={`text-xs ${isCurrent ? 'text-soem-primary font-semibold' : 'text-slate-400'}`}>
                    {item.date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isLider, activeUser } = useAuth();
  const canEdit = isAdmin || isLider;

  const [dossier, setDossier] = useState(null);
  const [dossierError, setDossierError] = useState(false);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('antes');
  const [leaderName, setLeaderName] = useState('');
  const [levName, setLevName] = useState('');
  const [clientName, setClientName] = useState('');

  const load = async () => {
    try {
      const r = await eventsAPI.get(id);
      // #Fase3-G4 — El líder del proyecto y el del evento comparten validación, otros líderes no.
      let projectLeader = null;
      if (activeUser?.role === 'lider_proyecto' && r.data.project_id) {
        const project = await projectsAPI.get(r.data.project_id).catch(() => null);
        projectLeader = project?.data?.leader_id || project?.data?.assigned_leader_id ||
          project?.data?.lider_id || null;
      }
      setEvent(current => ({ ...r.data, project_leader_id: projectLeader,
        permissions: r.data.permissions ?? (current?.id === r.data.id ? current.permissions : undefined),
      }));
      try {
        const response = await dossierAPI.get(id);
        setDossier(response.data);
        setEvent(current => ({ ...current, permissions: response.data.permissions ?? current?.permissions }));
        setDossierError(false);
      } catch {
        setDossier(null);
        setDossierError(true);
      }
      // Load user names
      const [usersRes, clientsRes] = await Promise.all([
        usersAPI.list().catch(() => ({ data: { items: [] } })),
        clientsAPI.list().catch(() => ({ data: { items: [] } })),
      ]);
      const users = usersRes.data.items || [];
      const clients = clientsRes.data.items || [];
      if (r.data.assigned_leader_id) {
        const u = users.find(u => u.id === r.data.assigned_leader_id);
        if (u) setLeaderName(u.name);
      }
      if (r.data.assigned_levantador_id) {
        const u = users.find(u => u.id === r.data.assigned_levantador_id);
        if (u) setLevName(u.name);
      }
      if (r.data.client_id) {
        const c = clients.find(c => c.id === r.data.client_id);
        if (c) setClientName(c.name);
      }
    } catch { toast.error('Error al cargar evento'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const changeStatus = async (status) => {
    try {
      await eventsAPI.changeStatus(id, status);
      toast.success('Status actualizado');
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Error al cambiar status');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" /></div>;
  if (!event) return <div className="text-center py-12 text-slate-400">Evento no encontrado.</div>;

  const et = EVENT_TYPES[event.event_type] || { emoji: '📅', label: event.event_type };
  const dates = event.dates || {};
  const validTransitions = STATUS_TRANSITIONS[event.status] || [];
  const loc = event.location;
  const audience = event.audience_profile || {};

  return (
    <div className="space-y-6 animate-fade-in" data-testid="event-detail">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button onClick={() => navigate('/dashboard/events')} className="soem-btn-secondary p-2 mt-1">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-soem-navy">{event.name}</h1>
            <EventStatusBadge status={event.status} />
            <RiskBadge level={event.projected_risk_level} />
            <span className="bg-slate-100 text-slate-400 text-xs px-2 py-0.5 rounded font-mono">{event.event_code}</span>
          </div>
          <p className="text-slate-500 text-sm mt-0.5">{et.emoji} {et.label} · {loc?.name || '—'}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {validTransitions.length > 0 && canEdit && (
            validTransitions.map(s => (
              <button key={s} onClick={() => changeStatus(s)}
                disabled={s === 'auditado' && !dossier?.audit_ready}
                className="soem-btn-secondary px-3 py-2 text-xs">
                → {EVENT_STATUS[s]?.label}
              </button>
            ))
          )}
          {canEdit && (
            <button onClick={() => navigate(`/dashboard/events/${id}/edit`)}
              className="soem-btn-secondary px-3 py-2 text-sm flex items-center gap-2" data-testid="edit-event-btn">
              <Edit2 size={14} /> Editar
            </button>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="soem-card p-5">
        <p className="soem-label mb-3">Cronograma del Evento</p>
        <Timeline dates={dates} />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {/* #UbicacionUnify — antes había 2 tabs separados ('emergencias' +
            'servicios'); ahora se fusionan en uno solo 'Ubicación' que
            muestra mapa + servicios cercanos + emergencias heredadas del
            venue, más extras del evento. */}
        {[
          ...Object.entries(STAGES).map(([id, label]) => ({ id, label })),
          { id: 'info', label: 'Información' },
          { id: 'proveedores', label: 'Proveedores' },
          { id: 'incidencias', label: 'Incidencias' },
          { id: 'evaluaciones', label: 'Evaluaciones' },
          { id: 'personal', label: 'Personal' },
          { id: 'ubicacion', label: 'Ubicación' },
        ].map(t => (
          <button 
            key={t.id} 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setActiveTab(t.id);
            }}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === t.id ? 'border-soem-primary text-soem-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {dossierError && <p role="alert">No se pudo cargar el expediente.
        <button onClick={load}>Reintentar</button></p>}
      {activeTab === 'despues' && <ReportDownload eventId={id} />}
      {STAGES[activeTab] && <Dossier dossier={dossier} stage={activeTab} event={event}
        user={activeUser} canEdit={canEdit} onChanged={load} />}
      {activeTab === 'proveedores' && <Providers eventId={id} eventName={event.name}
        canEdit={event.permissions?.can_manage_providers ?? canWork(activeUser, event)} onChanged={load} />}
      {activeTab === 'incidencias' && <Records event={event} user={activeUser} incidents onChanged={load} />}
      {/* Tab: Info */}
      {activeTab === 'info' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            {clientName && (
              <div><p className="soem-label mb-1">Promotor / Cliente</p>
                <p className="text-sm font-medium text-soem-navy">{clientName}</p></div>
            )}
            <div><p className="soem-label mb-1">Tipo de Evento</p>
              <p className="text-sm font-medium text-soem-navy">{et.emoji} {et.label}</p></div>
            <div><p className="soem-label mb-1">Riesgo Proyectado</p>
              <RiskBadge level={event.projected_risk_level} /></div>
            {event.expected_attendance && (
              <div><p className="soem-label mb-1 flex items-center gap-1"><Users size={11} /> Aforo Esperado</p>
                <p className="text-sm font-medium text-soem-navy">{event.expected_attendance.toLocaleString()} personas</p>
                {loc?.max_capacity && (
                  <div className="mt-1.5">
                    <div className="flex justify-between text-xs text-slate-400 mb-0.5">
                      <span>{((event.expected_attendance / loc.max_capacity) * 100).toFixed(1)}% del aforo máximo</span>
                      <span>Máx: {loc.max_capacity.toLocaleString()}</span>
                    </div>
                    <div className="bg-slate-100 rounded-full h-2">
                      <div className={`h-2 rounded-full transition-all ${(event.expected_attendance / loc.max_capacity) > 0.8 ? 'bg-orange-500' : 'bg-soem-primary'}`}
                        style={{ width: `${Math.min((event.expected_attendance / loc.max_capacity) * 100, 100)}%` }} />
                    </div>
                  </div>
                )}
              </div>
            )}
            {event.crowd_management && (
              <div><p className="soem-label mb-1">Manejo de Multitudes</p>
                <p className="text-sm font-medium text-soem-navy capitalize">{event.crowd_management}</p></div>
            )}
            {leaderName && (
              <div><p className="soem-label mb-1">Líder Asignado</p>
                <p className="text-sm font-medium text-soem-navy">{leaderName}</p></div>
            )}
            {levName && (
              <div><p className="soem-label mb-1">Levantador Asignado</p>
                <p className="text-sm font-medium text-soem-navy">{levName}</p></div>
            )}
            {event.notes && (
              <div><p className="soem-label mb-1 flex items-center gap-1"><FileText size={11} /> Notas</p>
                <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-sm border">{event.notes}</p></div>
            )}
          </div>

          <div className="space-y-4">
            {audience.age_ranges?.length > 0 && (
              <div><p className="soem-label mb-2">Rangos de Edad</p>
                <div className="flex flex-wrap gap-1.5">
                  {audience.age_ranges.map(r => (
                    <span key={r} className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full border border-blue-200">{r}</span>
                  ))}
                </div>
              </div>
            )}
            {audience.audience_types?.length > 0 && (
              <div><p className="soem-label mb-2">Tipos de Audiencia</p>
                <div className="flex flex-wrap gap-1.5">
                  {audience.audience_types.map(t => (
                    <span key={t} className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">{t.replace('_', ' ')}</span>
                  ))}
                </div>
              </div>
            )}
            {loc ? (
              <div className="soem-card p-4">
                <p className="soem-label mb-2">Locación</p>
                <p className="font-bold text-soem-navy">{loc.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{loc.address?.city}</p>
                {loc.max_capacity && <p className="text-xs text-slate-500">Aforo máx: {loc.max_capacity.toLocaleString()}</p>}
                <button onClick={() => navigate(`/dashboard/locations/${loc.id}`)}
                  className="text-soem-primary text-xs font-semibold mt-2 flex items-center gap-1 hover:underline">
                  <ExternalLink size={11} /> Ver locación
                </button>
              </div>
            ) : (
              <div className="soem-card p-4 bg-slate-50">
                <p className="soem-label mb-2">Locación</p>
                <p className="text-slate-400 text-sm">Sin locación asignada</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab: Evaluaciones */}
      {activeTab === 'evaluaciones' && (
        <EvaluationsTab eventId={id} canEdit={canEdit} />
      )}

      {/* Tab: Personal */}
      {activeTab === 'personal' && <EventStaffTab event={event} />}

      {/* #UbicacionUnify — Tab Ubicación reemplaza emergencias + servicios */}
      {activeTab === 'ubicacion' && (
        <UbicacionTab mode="event" eventId={id} location={loc} />
      )}
    </div>
  );
}
